import { Idiomorph } from "idiomorph";
import { BRAND_SITE_PREVIEW_MESSAGE } from "@/lib/brand-site-preview-nav";

/** 与 brand-site-preview-nav 注入的点选桥脚本一致，morph 时不得拆除。 */
export const PREVIEW_PICK_RUNTIME_ATTR = "data-c456-pick-runtime";

export type ApplyPreviewResult = "loaded" | "morphed";

/** iframe 这一帧该怎么画：加载中 hold（外层遮罩），勿往 srcdoc 写占位以免双写白屏。 */
export type PreviewIframePaint =
  | { mode: "hold" }
  | { mode: "document"; html: string };

/**
 * 决定预览 iframe 是否写入文档。
 * API 换页时先 hold + 外层「加载预览」，等真 HTML/错误再写一次 srcdoc。
 */
export function resolvePreviewIframePaint(input: {
  missingHtml: string | null;
  needsPreviewApi: boolean;
  previewHtml: string;
  previewError: string | null;
  staticHtml: string;
}): PreviewIframePaint {
  if (input.missingHtml != null) {
    return { mode: "document", html: input.missingHtml };
  }
  if (input.needsPreviewApi) {
    const html = input.previewHtml.trim();
    if (html) return { mode: "document", html };
    if (input.previewError) {
      const msg = input.previewError.replace(/[<>&]/g, "");
      return {
        mode: "document",
        html: `<p style="padding:2rem;font-family:system-ui,sans-serif;color:#b91c1c">${msg}</p>`,
      };
    }
    return { mode: "hold" };
  }
  return { mode: "document", html: input.staticHtml };
}

/** 占位 / 空预览 / 未展开 Liquid：下一帧须强制写 srcdoc，勿走 postMessage morph（易静默失败导致白屏或露出源码）。 */
export function isPreviewPlaceholderSrcDoc(html: string): boolean {
  const raw = html.trim();
  if (!raw) return true;
  if (raw.includes("加载预览")) return true;
  if (/\{%\s*render\b/i.test(raw)) return true;
  const withoutScripts = raw.replace(/<script[\s\S]*?<\/script>/gi, "");
  return /<body[^>]*>\s*<\/body>/i.test(withoutScripts);
}

/**
 * 是否必须整页写 srcdoc（勿 morph）。
 * 切进设计页偶发白屏：iframe 节点已换但 applied 仍命中 → 跳过写入；或把真 HTML morph 成空文档。
 * 换预览路径时也强制（跨页 morph / 双写 srcdoc 易白屏）。
 */
export function shouldForcePreviewSrcDocReload(
  previousApplied: string | null,
  nextSrcDoc: string,
  opts?: { pathChanged?: boolean },
): boolean {
  if (opts?.pathChanged) return true;
  if (previousApplied == null) return true;
  if (isPreviewPlaceholderSrcDoc(previousApplied)) return true;
  if (isPreviewPlaceholderSrcDoc(nextSrcDoc)) return true;
  return false;
}

function isPickRuntimeScript(node: Node): boolean {
  // 不用 instanceof：iframe 文档节点与父窗口 HTMLElement 跨 realm 会失败
  if (node.nodeType !== 1) return false;
  const el = node as Element;
  return el.tagName === "SCRIPT" && el.hasAttribute(PREVIEW_PICK_RUNTIME_ATTR);
}

function documentLooksEmpty(doc: Document): boolean {
  return (
    !doc.body.hasChildNodes() &&
    !(doc.head && doc.head.querySelector("style,link,script,title,meta"))
  );
}

/**
 * 用 Idiomorph 把下一版 HTML 合进已有 document（Turbo 8 同款思路）。
 * 保留滚动位置，并保护点选运行时 script。
 */
export function morphPreviewDocument(
  doc: Document,
  win: Window,
  nextSrcDoc: string,
): void {
  const scrollX = win.scrollX || 0;
  const scrollY = win.scrollY || 0;
  const nextDoc = new DOMParser().parseFromString(nextSrcDoc, "text/html");
  if (!nextDoc.documentElement) {
    return;
  }

  Idiomorph.morph(doc.documentElement, nextDoc.documentElement, {
    morphStyle: "outerHTML",
    head: { style: "morph" },
    callbacks: {
      beforeNodeRemoved(node) {
        if (isPickRuntimeScript(node)) return false;
        return true;
      },
      beforeNodeAdded(node) {
        if (
          isPickRuntimeScript(node) &&
          doc.querySelector(`script[${PREVIEW_PICK_RUNTIME_ATTR}]`)
        ) {
          return false;
        }
        return true;
      },
    },
  });

  win.scrollTo(scrollX, scrollY);
}

/**
 * 写入预览 iframe：首屏 / 强制刷新走 srcdoc；同页内容变更走 morph。
 *
 * 预览 iframe 使用 `sandbox="allow-scripts"`（无 allow-same-origin），父页
 * `contentDocument` 恒为 null，必须 postMessage 让帧内 Idiomorph 更新，否则会
 * 每次退回改 srcdoc → 整页重载 → 滚动回顶。
 */
export function applyPreviewSrcDoc(
  iframe: HTMLIFrameElement,
  nextSrcDoc: string,
  opts?: { forceReload?: boolean },
): ApplyPreviewResult {
  const win = iframe.contentWindow;
  if (!win || opts?.forceReload) {
    if (iframe.dataset) delete iframe.dataset.c456PreviewReady;
    iframe.srcdoc = nextSrcDoc;
    return "loaded";
  }

  const doc = iframe.contentDocument;
  if (doc?.documentElement && doc.body && !documentLooksEmpty(doc)) {
    morphPreviewDocument(doc, win, nextSrcDoc);
    return "morphed";
  }

  // sandbox：帧 onLoad 后打标，再 postMessage 做帧内 morph
  if (iframe.dataset.c456PreviewReady === "1") {
    win.postMessage(
      {
        source: BRAND_SITE_PREVIEW_MESSAGE,
        type: "apply-html",
        html: nextSrcDoc,
      },
      "*",
    );
    return "morphed";
  }

  iframe.srcdoc = nextSrcDoc;
  return "loaded";
}

/** iframe onLoad 后调用，允许后续同页更新走 postMessage morph。 */
export function markPreviewIframeReady(iframe: HTMLIFrameElement | null): void {
  if (!iframe) return;
  iframe.dataset.c456PreviewReady = "1";
}
